import { Injectable, Inject, Injector, Signal } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { GroupMemberDto } from 'src/app/travel-contracts/shared/models/group-member-dto';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { TravelContract } from 'src/app/travel-contracts/travel-contracts-grid/models/travel-contract';
import { HousingOptions } from 'src/app/travel-contracts/contract-profile/shared/models/housing-options.model';
import { HousingStipend } from 'src/app/travel-contracts/contract-profile/shared/models/housing-stipend.model';
import { HousingFamilyMember } from 'src/app/travel-contracts/contract-profile/shared/models/housing-family-member.model';
import { HousingAnimal } from 'src/app/travel-contracts/contract-profile/shared/models/housing-animal.model';
import { HousingFurnitureItem } from 'src/app/travel-contracts/contract-profile/shared/models/housing-furniture-item';
import { ContractBillingInfo } from 'src/app/travel-contracts/contract-profile/shared/models/contract-billing-info.model';
import { ContractQueueDto } from 'src/app/travel-contracts/shared/models/contract-queue-dto';
import { Observable } from 'rxjs';
import { ContractExtraSubType } from './contract-profile/shared/models/contract-extra-sub-type.model';
import { Schedule } from './contract-profile/shared/models/schedule.model';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { ContractAgency } from 'src/app/travel-contracts/travel-contracts-grid/models/contract-agency.model';
import { TravelContractPermissions } from 'src/app/travel-contracts/models/travel-contract-permissions.model';
import { CheckContractAssignment } from './shared/models/check-contract-assignment.model';
import { combine } from 'src/app/shared/utilities/route';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { ContractStatus } from 'src/app/travel-contracts/travel-contracts-grid/models/contract-status';
import { TravelContractFacilitiesPermissions } from 'src/app/travel-contracts/models/travel-contract-facilities-permissions.model';
import { TravelContractCandidate } from './candidates/travel-contract-candidate.model';
import { TravelContractCandidateGridLookup } from './candidates/travel-contract-candidate-grid-lookup.model';
import { ContractQueueCountsDto } from './shared/models/contract-queue-counts-dto';
import { CandidateExport } from './models/candidate-export.model';
import { FacilityContactGridItem } from 'src/app/shared/lib/travel-contracts-contacts/models/facility-contact-grid-item.model';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';

@Injectable({
    providedIn: 'root'
})
export class TravelContractsService {
    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings,
        private readonly featureManager: LDFeatureManager,
        private readonly injector: Injector
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
        this.intranetApiPath = combine(settings.coreAPIUrl, 'intranet');

        this.linkLocums1099MarginsToCandidatePayroll = toSignal(
            this.featureManager.isEnabled(FeatureFlag.LinkLocums1099MarginsToCandidatePayroll),
            {
                injector: this.injector,
                initialValue: false
            }
        );
    }

    private readonly coreApiPath: string;
    private readonly intranetApiPath: string;
    private readonly linkLocums1099MarginsToCandidatePayroll: Signal<boolean>;

    searchContracts(args: FetchArgs): Observable<PagingToken<ContractQueueDto[]>> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/search`;
        return this.http.post<PagingToken<ContractQueueDto[]>>(url, args);
    }

    getCounts(args: FetchArgs): Observable<ContractQueueCountsDto> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/counts`;
        return this.http.post<ContractQueueCountsDto>(url, args);
    }

    getContacts(facilityId: number, args: FetchArgs): Observable<PagingToken<FacilityContactGridItem[]>> {
        const facilityIdParam = 'filter.facilityId';
        const nameParam = 'filter.name';
        const titleParam = 'filter.title';
        const pageSizePram = 'pageSize';
        const skipParam = 'skip';

        const params = new HttpParams()
            .set(pageSizePram, args.pageSize.toString())
            .set(skipParam, args.skip.toString())
            .set(facilityIdParam, facilityId.toString())
            .set(nameParam, args.filter.name ?? '')
            .set(titleParam, args.filter.title ?? '');

        const url = `${this.coreApiPath}/travel-contract/facilities/${facilityId}/contacts`;
        return this.http.get<PagingToken<FacilityContactGridItem[]>>(url, { params: params });
    }

    getFacilitiesPermissions(facilityId: number): Observable<TravelContractFacilitiesPermissions> {
        const url = `${this.coreApiPath}/travel-contract/facilities/${facilityId}/permissions`;
        return this.http.get<TravelContractFacilitiesPermissions>(url);
    }

    getPermissions(): Observable<TravelContractPermissions> {
        const url = `${this.coreApiPath}/travel-contracts/permissions`;
        return this.http.get<TravelContractPermissions>(url);
    }

    getContractById(contractId: string): Observable<TravelContract> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}`;
        return this.http.get<TravelContract>(url);
    }

    getAgency(contractId: number): Observable<ContractAgency> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/agency`;
        return this.http.get<ContractAgency>(url);
    }

    getBillingInfo(contractId: string): Observable<ContractBillingInfo> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/billing-info`;
        return this.http.get<ContractBillingInfo>(url);
    }

    saveBillingInfo(contractId: string, info: ContractBillingInfo): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/billing-info`;
        return this.http.post<number>(url, info);
    }

    assignContracts(data: any): Observable<any> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/assign-contracts`;
        return this.http.post<number>(url, data);
    }

    assignToMeContracts(data: any): Observable<any> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/assign-to-me-contracts`;
        return this.http.post<number>(url, data);
    }

    checkContractsAssignment(requestsIds: number[]): Observable<CheckContractAssignment[]> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/check-contracts-assignment`;
        return this.http.post<CheckContractAssignment[]>(url, requestsIds);
    }

    rejectContracts(data: any): Observable<any> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/reject-contracts`;
        return this.http.post<number>(url, data);
    }

    getGroupMembers(): Observable<GroupMemberDto[]> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/group-members`;
        return this.http.get<GroupMemberDto[]>(url);
    }

    searchNurseContracts(filter: FetchArgs): Observable<PagingToken<TravelContract[]>> {
        const url = `${this.coreApiPath}/candidate/contracts/search`;

        if (this.linkLocums1099MarginsToCandidatePayroll()) {
            return this.http.post<PagingToken<TravelContract[]>>(url, filter).pipe(
                map((paging) => {
                    const mapped = {
                        ...paging,
                        data: paging.data.map((travelContract) => {
                            const hasLocums1099MarginId = travelContract.locums1099MarginId != null;
                            return {
                                ...travelContract,
                                hasLocums1099Margin: hasLocums1099MarginId,
                                effectiveMarginId: hasLocums1099MarginId
                                    ? travelContract.locums1099MarginId
                                    : (travelContract.marginId ?? null)
                            };
                        })
                    };

                    return mapped;
                })
            );
        }

        return this.http.post<PagingToken<TravelContract[]>>(url, filter);
    }

    exportNurseContracts(filter: FetchArgs, isPayroll: boolean): Observable<HttpResponse<ArrayBuffer>> {
        const url = isPayroll
            ? `${this.coreApiPath}/candidate/contracts/payroll/export`
            : `${this.coreApiPath}/candidate/contracts/export`;
        return this.http.post(url, filter, { observe: 'response', responseType: 'arraybuffer' });
    }

    getHousingOptions(contractId: number): Observable<HousingOptions> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-options`;
        return this.http.get<HousingOptions>(url);
    }

    saveHousingOptions(contractId: number, options: HousingOptions): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-options`;
        return this.http.post<number>(url, options);
    }

    getHousingStipends(contractId: number): Observable<HousingStipend[]> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-stipends`;
        return this.http.get<HousingStipend[]>(url);
    }

    getHousingFamilyMembers(contractId: number): Observable<HousingFamilyMember[]> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-family-members`;
        return this.http.get<HousingFamilyMember[]>(url);
    }

    getHousingAnimals(contractId: number): Observable<HousingAnimal[]> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-animals`;
        return this.http.get<HousingAnimal[]>(url);
    }

    getHousingFurnitureItems(contractId: number): Observable<HousingFurnitureItem[]> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-furniture-items`;
        return this.http.get<HousingFurnitureItem[]>(url);
    }

    saveHousingFamilyMember(contractId: number, member: HousingFamilyMember): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-family-members`;
        return this.http.post<number>(url, member);
    }

    removeHousingFamilyMember(contractId: number, id: number): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-family-members/${id}`;
        return this.http.delete<number>(url);
    }

    saveHousingPet(contractId: number, member: HousingAnimal): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-animals`;
        return this.http.post<number>(url, member);
    }

    removeHousingPet(contractId: number, id: number): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-animals/${id}`;
        return this.http.delete<number>(url);
    }

    saveHousingFurnitureItem(contractId: number, furniture: HousingFurnitureItem): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-furniture-items`;
        return this.http.post<number>(url, furniture);
    }

    removeHousingFurnitureItem(contractId: number, id: number): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-furniture-items/${id}`;
        return this.http.delete<number>(url);
    }

    saveHousingStipend(contractId: number, stipend: HousingStipend): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-stipends`;
        return this.http.post<number>(url, stipend);
    }

    removeHousingStipend(contractId: number, id: number): Observable<number> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/housing-stipends/${id}`;
        return this.http.delete<number>(url);
    }

    getStipendTypes(contractId: number): Observable<ContractExtraSubType[]> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/stipend-types`;
        return this.http.get<ContractExtraSubType[]>(url);
    }

    getSchedules(contractId: number): Observable<Schedule[]> {
        const url = `${this.coreApiPath}/candidate/contracts/${contractId}/schedules`;
        return this.http.get<Schedule[]>(url);
    }

    downloadDocument(documentId: number): Observable<any> {
        const url = `${this.intranetApiPath}/documents/${documentId}`;
        return this.http.get(url, { responseType: 'blob' as 'json' });
    }

    getRecruiters(): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/candidate/contracts/recruiters`;
        return this.http.get<Lookup[]>(url);
    }

    getLocumsPayrollReps(): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/facility-contracts/facility-systems/payroll-liaisons`;
        return this.http.get<Lookup[]>(url);
    }

    getContractStages(lineOfBusinessId: number) {
        return this.http.get<Lookup[]>(`${this.coreApiPath}/candidate/contracts/lobs/${lineOfBusinessId}/stages`);
    }

    getContractStatuses() {
        return this.http.get<ContractStatus[]>(`${this.coreApiPath}/candidate/contracts/contract-statuses`);
    }

    getContractRequestStatusOptions() {
        return this.http.get<Lookup[]>(`${this.coreApiPath}/nurse-contracts/contract-queue/contract-statuses`);
    }

    updateContractsStage(data: any): Observable<any> {
        return this.http.put(`${this.coreApiPath}/candidate/contracts/stages`, data);
    }

    getBrands(): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, '/facility-contracts/system/brands');
        return this.http.get<Lookup[]>(url);
    }

    search(args: FetchArgs): Observable<PagingToken<TravelContractCandidate[]>> {
        // special case
        const prop = 'isSubcontractor';
        if (args.filter[prop]) {
            this.convertFilter(args.filter, prop);
        }
        return this.postPaged(`${this.coreApiPath}/travel-contract/candidates`, args);
    }

    gridLookup(): Observable<TravelContractCandidateGridLookup> {
        const url = `${this.coreApiPath}/travel-contract/candidates/lookups`;
        return this.http.get<TravelContractCandidateGridLookup>(url);
    }

    lookupAgency(searchText: string): Observable<Lookup[]> {
        const filter = '/agency?keyword=';

        const url = combine(`${this.coreApiPath}/travel-contract/candidates`, filter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    lookupExpertise(searchText: string): Observable<Lookup[]> {
        const filter = '/expertise?keyword=';

        const url = combine(`${this.coreApiPath}/travel-contract/candidates`, filter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    getNurseContractsUserId = (): Observable<number> =>
        this.http.get<number>(`${this.coreApiPath}/contract-requests/userid`);

    getCandidateExportInfo(candidateId: number): Observable<CandidateExport> {
        return this.http.get<CandidateExport>(`${this.coreApiPath}/candidate/${candidateId}/export`);
    }

    protected postPaged<T>(url: string, fetchArgs: FetchArgs): Observable<PagingToken<T[]>> {
        return this.http.post<PagingToken<T[]>>(url, fetchArgs, { observe: 'response' }).pipe(
            map((response) => {
                return {
                    total: response.body.total,
                    data: response.body.data
                };
            })
        );
    }

    private convertFilter(filter: CompositeFilterDescriptor, prop) {
        const val = filter[prop];
        if (val.length === 0) {
            delete filter[prop];
        } else if (val.length > 0) {
            filter[prop] = filter[prop].join() === 'true';
        }
    }
}
